import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Z } from '../../styles/zLayers';

interface DraggablePreviewPanelProps {
  title: React.ReactNode;
  /** Mono caption under the body (e.g. "This is the image that will be published"). */
  caption?: React.ReactNode;
  /** Extra header controls, left of the close button (e.g. Re-render). */
  actions?: React.ReactNode;
  /** Source media rect (viewport coords). When given, the panel's content
      width matches it (1:1 render comparison) and the panel spawns beside
      it with the media tops aligned. */
  anchor?: { width: number; top: number; left: number; right: number } | null;
  onClose: () => void;
  children: React.ReactNode;
}

const PAD = 18; // card padding
const HEADER_H = 36; // title row + its margin — media sits PAD + HEADER_H below the panel top

/**
 * Floating TEND-ink card for render-preview comparisons. Deliberately NO
 * veil (unlike TendModal): the panel floats over the live page so the
 * rendered output can be dragged next to its source card and compared
 * side-by-side. Drag by the header (buttons excluded); Escape or the X
 * closes. Body portal per the TendModal contract — it escapes every
 * backdrop-filter containing block.
 */
const DraggablePreviewPanel: React.FC<DraggablePreviewPanelProps> = ({
  title,
  caption,
  actions,
  anchor,
  onClose,
  children,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  // Sized once at mount: content width mirrors the source media when an
  // anchor is given, so the render displays at the exact same scale.
  const [panelWidth] = useState(() =>
    anchor
      ? Math.min(anchor.width + PAD * 2, Math.floor(window.innerWidth * 0.94))
      : Math.min(520, Math.floor(window.innerWidth * 0.45))
  );
  // Deterministic spawn — no transform involved, so the .tend-modal-card
  // rise animation can't fight the position. With an anchor: beside the
  // source (right if it fits, else left), media tops aligned.
  const [pos, setPos] = useState(() => {
    if (anchor) {
      let x = anchor.right + 12;
      if (x + panelWidth > window.innerWidth - 8) x = anchor.left - panelWidth - 12;
      x = Math.min(Math.max(x, 8), Math.max(8, window.innerWidth - panelWidth - 8));
      const y = Math.min(Math.max(anchor.top - (PAD + HEADER_H), 8), Math.max(8, window.innerHeight - 160));
      return { x, y };
    }
    return {
      x: Math.max(8, window.innerWidth - panelWidth - 16),
      y: Math.round(window.innerHeight * 0.08),
    };
  });
  const dragRef = useRef<{ dx: number; dy: number; w: number; h: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button')) return;
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top, w: rect.width, h: rect.height };
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d) return;
    setPos({
      x: Math.min(Math.max(e.clientX - d.dx, 8), Math.max(8, window.innerWidth - d.w - 8)),
      y: Math.min(Math.max(e.clientY - d.dy, 8), Math.max(8, window.innerHeight - d.h - 8)),
    });
  };

  const endDrag = () => {
    dragRef.current = null;
    setDragging(false);
  };

  return createPortal(
    <div
      ref={panelRef}
      className="tend-surface tend-f2 tend-modal-card"
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        // Above every app modal (tended modal portals at 999999) but below
        // the transient popover/menu layer (1000001+, e.g. smart-publish menu)
        zIndex: Z.ABOVE_ITEM,
        width: panelWidth,
        maxHeight: '85vh',
        borderRadius: 20,
        padding: PAD,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 12,
          cursor: dragging ? 'grabbing' : 'grab',
          touchAction: 'none',
          userSelect: 'none',
        }}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontFamily: "'Inter Tight', sans-serif",
            fontWeight: 700,
            fontSize: 17,
            letterSpacing: '-.01em',
            color: 'var(--text)',
          }}
        >
          {title}
        </span>
        {actions}
        <button
          type="button"
          className="tend-btn tend-btn--icon tend-btn--icon-sm"
          aria-label="Close"
          onClick={onClose}
          style={{ flexShrink: 0 }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>
      <div className="tend-scroll" style={{ overflowY: 'auto', minHeight: 0 }}>
        {children}
      </div>
      {caption && (
        <div
          className="tend-micro"
          style={{ textTransform: 'none', letterSpacing: 0.4, textAlign: 'center', marginTop: 10 }}
        >
          {caption}
        </div>
      )}
    </div>,
    document.body
  );
};

export default DraggablePreviewPanel;
